import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconEdit, IconTrash } from '../components/icons';
import { labelFor, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { formatDateTime } from '../utils/format';

// TS port of the source (Falah) app's pages/BookingDetail.jsx.
interface BookingRecord {
  id: number;
  title: string;
  booked_by: string | null;
  start_at: string;
  end_at: string;
  status: string;
  notes: string | null;
  facility: { name: string } | null;
}

export function BookingDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lists } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [booking, setBooking] = useState<BookingRecord | null>(null);

  useEffect(() => {
    api.get<BookingRecord>(`/bookings/${id}`).then((res) => setBooking(res.data));
  }, [id]);

  const remove = async () => {
    if (!booking) return;
    if (!(await confirmDelete(`Delete booking "${booking.title}"?`))) return;
    try {
      await api.delete(`/bookings/${id}`);
      navigate('/resources/bookings');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!booking) return null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/resources/bookings')} />
          <IconButton icon={IconEdit} label="Edit" to={`/resources/bookings/${id}/edit`} />
          <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={remove} />
        </div>
      </header>

      <div className="card">
        <div className="detail-summary">
          <div>
            <div className="detail-summary-title">{booking.title}</div>
            <span className={`badge badge-${booking.status === 'confirmed' ? 'normal' : 'important'}`}>
              {labelFor(lists, 'booking_status', booking.status)}
            </span>
            <div className="detail-summary-service">{booking.facility?.name ?? 'Unknown facility'}</div>
          </div>
        </div>

        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-item-label">Start</span>
            <span className="detail-item-value">{formatDateTime(booking.start_at)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">End</span>
            <span className="detail-item-value">{formatDateTime(booking.end_at)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-item-label">Booked by</span>
            <span className={`detail-item-value${booking.booked_by ? '' : ' empty'}`}>
              {booking.booked_by || 'Not set'}
            </span>
          </div>
        </div>

        <div className="detail-item detail-item-full">
          <span className="detail-item-label">Notes</span>
          {booking.notes ? (
            <p className="detail-notes">{booking.notes}</p>
          ) : (
            <span className="detail-item-value empty">Not provided</span>
          )}
        </div>
      </div>
    </>
  );
}
